import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import { startSyncEngine } from './services/syncEngine';
import SyncStatusBanner from './components/SyncStatusBanner';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import TherapistPage from './pages/TherapistPage/TherapistPage';
import ChildPage from './pages/ChildPage/ChildPage';
import ExerciseHub from './pages/ExerciseHub/ExerciseHub';
import AuditoryExercisePage from './pages/AuditoryExercisePage/AuditoryExercisePage';
import PracticeBuddyPage from './pages/PracticeBuddyPage/PracticeBuddyPage';
import OralMotorPage from './pages/OralMotorPage/OralMotorPage';
import IntakeHub from './pages/IntakeHub/IntakeHub';
import ConsentPage from './pages/ConsentPage/ConsentPage';
import CaseHistoryPage from './pages/CaseHistoryPage/CaseHistoryPage';
import CapturePage from './pages/CapturePage/CapturePage';
import './App.css';

function App() {
  useEffect(() => {
    startSyncEngine(); // auto-sync queued offline recordings when network returns
  }, []);

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <SyncStatusBanner />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Protected routes */}
            <Route
              path="/therapist"
              element={
                <ProtectedRoute requiredRole="therapist">
                  <TherapistPage />
                </ProtectedRoute>
              }
            />

            {/* Case History — intake assistant's own shell */}
            <Route
              path="/intake"
              element={
                <ProtectedRoute allowRoles={['intake_worker', 'therapist']}>
                  <IntakeHub />
                </ProtectedRoute>
              }
            />

            <Route
              path="/intake/consent/:caseId"
              element={
                <ProtectedRoute allowRoles={['intake_worker', 'therapist']}>
                  <ConsentPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/intake/case-history/:caseId"
              element={
                <ProtectedRoute allowRoles={['intake_worker', 'therapist']}>
                  <CaseHistoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/intake/capture/:sessionId"
              element={
                <ProtectedRoute allowRoles={['intake_worker', 'therapist']}>
                  <CapturePage />
                </ProtectedRoute>
              }
            />

            {/* The word list inside a session: the child's own page, but the
                examiner is the one logged in, so it lives under /intake. */}
            <Route
              path="/intake/capture/:sessionId/words"
              element={
                <ProtectedRoute allowRoles={['intake_worker', 'therapist']}>
                  <ChildPage />
                </ProtectedRoute>
              }
            />

            {/* Child routes - Hub is the main entry */}
            <Route
              path="/child"
              element={
                <ProtectedRoute requiredRole="child">
                  <ExerciseHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/child/hub"
              element={
                <ProtectedRoute requiredRole="child">
                  <ExerciseHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/child/articulation"
              element={
                <ProtectedRoute requiredRole="child">
                  <ChildPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/child/auditory"
              element={
                <ProtectedRoute requiredRole="child">
                  <AuditoryExercisePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/child/sound-id"
              element={<Navigate to="/child/auditory" replace />}
            />
            <Route
              path="/child/buddy"
              element={
                <ProtectedRoute requiredRole="child">
                  <PracticeBuddyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/child/oral-motor"
              element={
                <ProtectedRoute requiredRole="child">
                  <OralMotorPage />
                </ProtectedRoute>
              }
            />

            {/* Default redirect to login */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;

